import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { ApiError } from '@/shared/api/api-error';

import { adminLogin, adminLogout, fetchAdminMe } from './admin-auth.api';
import { setAdminUnauthorizedHandler } from './admin-client';
import { adminToken } from './admin-token';
import { AdminAuthContext } from './AdminAuthContext';

export function AdminAuthProvider({ children }) {
  const [admin, setAdmin] = useState(null);
  const [status, setStatus] = useState('checking');
  const queryClient = useQueryClient();

  const clearSession = useCallback(() => {
    adminToken.clear();
    setAdmin(null);
    setStatus('anonymous');
    queryClient.removeQueries({ queryKey: ['admin'] });
  }, [queryClient]);

  // Restore a persisted admin session on mount.
  useEffect(() => {
    let active = true;

    if (!adminToken.get()) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- one-shot mount session restore; no persisted token means resolve straight to anonymous
      setStatus('anonymous');
      return;
    }

    fetchAdminMe()
      .then((user) => {
        if (!active) return;
        if (user?.role === 'admin') {
          setAdmin(user);
          setStatus('authenticated');
        } else {
          clearSession();
        }
      })
      .catch(() => {
        if (active) clearSession();
      });

    return () => {
      active = false;
    };
  }, [clearSession]);

  // A 401 from any admin request drops the session.
  useEffect(() => {
    setAdminUnauthorizedHandler(() => {
      setAdmin(null);
      setStatus('anonymous');
      queryClient.removeQueries({ queryKey: ['admin'] });
    });
    return () => setAdminUnauthorizedHandler(undefined);
  }, [queryClient]);

  const login = useCallback(async (credentials) => {
    const { user, token } = await adminLogin(credentials);

    if (user?.role !== 'admin') {
      throw new ApiError({
        message: 'This account does not have admin access.',
        status: 403,
        code: 'NOT_ADMIN',
      });
    }

    adminToken.set(token);
    setAdmin(user);
    setStatus('authenticated');
    return user;
  }, []);

  // Re-fetch the admin identity, e.g. after an email change.
  const refresh = useCallback(async () => {
    try {
      const user = await fetchAdminMe();
      if (user?.role === 'admin') setAdmin(user);
    } catch {
      /* best-effort; a 401 is handled by the interceptor */
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await adminLogout();
    } catch {
      /* revoke best-effort — clear locally regardless */
    }
    clearSession();
  }, [clearSession]);

  const value = useMemo(
    () => ({ admin, status, login, logout, refresh }),
    [admin, status, login, logout, refresh],
  );

  return <AdminAuthContext value={value}>{children}</AdminAuthContext>;
}
